import type { AlreadyRead } from './valueObjects/alreadyRead';
import type { ChildId } from './valueObjects/childId';
import type { Comment } from './valueObjects/comment';
import type { ConditionNote } from './valueObjects/conditionNote';
import type { ConditionType } from './valueObjects/conditionType';
import type { IsSent } from './valueObjects/isSent';
import type { MoodCategory } from './valueObjects/moodCategory';
import type { ParentId } from './valueObjects/parentId';
import type { RecordDate } from './valueObjects/recordDate';
import type { RecorderId } from './valueObjects/recorderId';
import type { RecorderName } from './valueObjects/recorderName';
import type { RecorderType } from './valueObjects/recorderType';
import type { PageJsonPub, PageJsonPvt } from '@/types/domain/childNotebook/entities/pageEntity';

import {
  EntityPub,
} from '@/domain/entityPub';
import { DeleteType } from '@/domain/entityRepositoryBase';
import {
  EntityName,
} from '@/infra/appsync';

/**
 * ページプロパティ
 */
export type PagePropsPvt = {
  /** 親ID */
  parentId: ParentId,
  /** 子ID */
  childId: ChildId,
  /** 記録者ID */
  recorderId: RecorderId,
  /** 記録者タイプ(app/nursery) */
  recorderType: RecorderType,
  /** 記録者名 */
  recorderName: RecorderName,
  /** 記録日 */
  recordDate: RecordDate,
  /** 機嫌種別 */
  moodCategory: MoodCategory,
  /** 体調 */
  conditionType?: ConditionType,
  /** 体調メモ */
  conditionNote?: ConditionNote,
  /** コメント */
  comment: Comment,
  /** 画像リスト */
  images: string[],
  /** 添付リスト */
  attachments: string[],
  /** 送信済みフラグ */
  isSent: IsSent,
}

/**
 * ページ公開プロパティ
 */
export type PagePropsPub = {
  /** 既読 */
  alreadyRead: AlreadyRead,
}

/**
 * ページEntity
 */
export class PageEntity extends EntityPub<
  PagePropsPvt,
  PageJsonPvt,
  PagePropsPub,
  PageJsonPub
> {
  /**
   * 削除タイプ
   */
  get deleteType(): DeleteType {
    return DeleteType.ParentChildId;
  }

  /**
   * 削除キー
   */
  get deleteKey(): string {
    return this.parentChildId;
  }

  /**
   * データJsonを返す
   * @returns データ
   */
  toJsonData(): PageJsonPvt {
    const images = [];
    for (let i = 0; i < this.images.length; i += 1) {
      images.push(this.images[i]);
    }
    const attachments = [];
    for (let i = 0; i < this.attachments.length; i += 1) {
      attachments.push(this.attachments[i]);
    }
    return {
      parentId: this.parentId.value,
      childId: this.childId.value,
      recorderId: this.recorderId.value,
      recorderType: this.recorderType.value,
      recorderName: this.recorderName.value,
      recordDate: this.recordDate.toStringDate(),
      moodCategory: this.moodCategory.value,
      conditionType: this.conditionType?.value,
      conditionNote: this.conditionNote?.value,
      comment: this.comment.value,
      images,
      attachments,
      isSent: this.isSent.value,
    };
  }

  /**
   * 公開データJsonを取得
   * @returns データ
   */
  toJsonPubData(): PageJsonPub {
    return {
      alreadyRead: this.alreadyRead.value,
    };
  }

  /**
   * Entity名
   */
  get entityName(): EntityName {
    const name: EntityName = EntityName.WagakoNurseryChildNotebookPageEntity;
    return name;
  }

  /**
   * Entityキー
   */
  get entityKey(): string {
    return `${this.recordDate.toStringDate()}#${this.recorderType.value}#${this.parentId.value}_${this.childId.value}`;
  }

  /**
   * サブキー(子どもを指定して検索)
   */
  get subKey(): string {
    return `${this.parentId.value}_${this.childId.value}#${this.recordDate.toStringDate()}#${this.recorderType.value}#${this.recorderId.value}`;
  }

  /**
   * S3フォルダパス
   */
  get s3FolderPath(): string {
    return `${this.entityName}/${this.entityKey}/`;
    // WagakoNurseryChildNotebookPageEntityは実際にはParentIdでパスを切った先に保存される
    // `${entityName}/${this.parentId.value}/${this.entityKey}/`
  }

  /**
   * 親子ID
   */
  get parentChildId() {
    return `${this.parentId.value}_${this.childId.value}`;
  }

  /**
   * 親ID
   */
  get parentId(): ParentId {
    return this._value.parentId;
  }

  /**
   * 子ID
   */
  get childId(): ChildId {
    return this._value.childId;
  }

  /**
   * 記録者ID
   */
  get recorderId(): RecorderId {
    return this._value.recorderId;
  }

  /**
   * 記録者タイプ
   */
  get recorderType(): RecorderType {
    return this._value.recorderType;
  }

  /**
   * 記録者名
   */
  get recorderName(): RecorderName {
    return this._value.recorderName;
  }

  /**
   * 記録日
   */
  get recordDate(): RecordDate {
    return this._value.recordDate;
  }

  /**
   * 機嫌
   */
  get moodCategory(): MoodCategory {
    return this._value.moodCategory;
  }

  /**
   * 体調
   */
  get conditionType(): ConditionType | undefined {
    return this._value.conditionType;
  }

  /**
   * 体調メモ
   */
  get conditionNote(): ConditionNote | undefined {
    return this._value.conditionNote;
  }

  /**
   * コメント
   */
  get comment(): Comment {
    return this._value.comment;
  }

  /**
   * 画像リスト
   */
  get images(): string[] {
    return this._value.images;
  }

  /**
   * 添付
   */
  get attachments(): string[] {
    return this._value.attachments;
  }

  /**
   * 送信済みフラグ
   */
  get isSent(): IsSent {
    return this._value.isSent;
  }

  /**
   * 既読
   */
  get alreadyRead(): AlreadyRead {
    return this._valuePubData.data.alreadyRead;
  }

  /**
   * 既読をセットする
   * @param alreadyRead 既読
   */
  setAlreadyRead(alreadyRead: AlreadyRead) {
    this._valuePubData.data.alreadyRead = alreadyRead;
  }
}
